/**
 * Utility to transform Azure Blob Storage URLs to use the proxy API
 *
 * When accessing files from Azure Blob Storage, we route them through
 * our backend proxy to handle authentication and avoid CORS issues.
 * Includes fallback to original URL if proxy fails.
 *
 * Azure blob URLs use the serve proxy with unsigned URLs (query stripped); the backend
 * loads blobs with server-side credentials. Video/audio with SAS still uses the direct URL
 * for Range-friendly streaming unless forced.
 */

import { API_SERVICE_BACKEND } from "src/config";
import gatewayAxios from "src/lib/axios";
import { addTokenToUrl } from "./add-token-to-url";

const PROXY_API_BASE = `${API_SERVICE_BACKEND}/files/serve`;

// Azure Blob Storage domains that should be proxied
const BLOB_STORAGE_PATTERNS = [
  ".blob.core.windows.net",
  "blob.storage.azure.net",
];

// Only these should keep direct Azure + SAS (Range streaming); images should use the serve proxy
const STREAMING_MEDIA_PATH = /\.(mp4|webm|ogv|ogg|m3u8|mp3|wav|m4a|aac|flac)(\?|#|$)/i;

/**
 * Checks if the given URL is an Azure Blob Storage URL
 * @param {string} url - The URL to check
 * @returns {boolean} - True if the URL is from Azure Blob Storage
 */
export const isAzureBlobUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  return BLOB_STORAGE_PATTERNS.some((pattern) => url.includes(pattern));
};

/**
 * Checks if the URL has a SAS token (Shared Access Signature)
 * SAS tokens allow direct access without proxy
 * @param {string} url - The URL to check
 * @returns {boolean} - True if URL has a SAS token
 */
export const hasSasToken = (url) => {
  if (!url || typeof url !== "string") return false;
  // SAS tokens typically have these query params
  return url.includes("sv=") && (url.includes("sig=") || url.includes("se="));
};

/**
 * Removes SAS / query string from an Azure blob URL so the serve endpoint can
 * load the object with server-side credentials (same as storing clean URLs in DB).
 */
export const stripAzureBlobSas = (url) => {
  if (!url || typeof url !== "string" || !isAzureBlobUrl(url)) return url;
  const q = url.indexOf("?");
  return q === -1 ? url : url.slice(0, q);
};

const isAzureStreamingPath = (url) => {
  if (!url || typeof url !== "string") return false;
  const path = url.split("?")[0];
  return STREAMING_MEDIA_PATH.test(path);
};

/**
 * Resolves a display or proxied URL back to the canonical blob URL for delete/update APIs
 * (e.g. extracts inner `url` from /files/serve?url=... or strips SAS from Azure URLs).
 * @param {string} url
 * @returns {string}
 */
const decodeInnerServeBlobUrl = (serveUrl) => {
  if (!serveUrl || typeof serveUrl !== "string" || !serveUrl.includes("/files/serve")) {
    return null;
  }
  try {
    const parsed = new URL(
      serveUrl,
      typeof window !== "undefined" ? window.location.origin : "https://app.b2brocket.ai"
    );
    const inner = parsed.searchParams.get("url");
    return inner && inner.trim() ? inner : null;
  } catch (e) {
    return null;
  }
};

export const getCanonicalStorageUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  const inner = decodeInnerServeBlobUrl(url);
  if (inner) {
    return stripAzureBlobSas(inner);
  }
  if (isAzureBlobUrl(url)) {
    return stripAzureBlobSas(url);
  }
  return url;
};

/**
 * Transforms an Azure Blob Storage URL to use the proxy API
 * If the URL is not from Azure Blob Storage, returns it unchanged
 * Unsigned blob URL in ?url= (server-side access on serve). Video/audio with SAS: direct URL unless forced.
 *
 * @param {string} url - The original URL
 * @param {object} options - Options
 * @param {boolean} options.forceProxy - Force proxy even if SAS token exists (for downloads)
 * @param {boolean} options.inline - Add disposition=inline so backend returns inline (preview) instead of attachment (download)
 * @returns {string} - The proxied URL or original URL if not applicable
 */
export const getBlobUrl = (url, options = {}) => {
  if (!url || typeof url !== "string") return url;

  let resolved = url;
  const innerFromServe = decodeInnerServeBlobUrl(url);
  if (innerFromServe) {
    resolved = innerFromServe;
  }

  const forProxy = stripAzureBlobSas(resolved);
  if (!forProxy || !isAzureBlobUrl(forProxy)) {
    return url;
  }

  if (
    hasSasToken(resolved) &&
    !options.forceProxy &&
    isAzureStreamingPath(resolved)
  ) {
    return resolved;
  }

  try {
    const base = `${PROXY_API_BASE}?url=${encodeURIComponent(forProxy)}`;
    return options.inline ? `${base}&disposition=inline` : base;
  } catch (e) {
    console.warn("getBlobUrl: Failed to encode URL, using original", e);
    return url;
  }
};

/**
 * Fetches content from a URL, using the proxy API for Azure Blob Storage URLs
 * Falls back to direct URL if proxy fails
 * Always uses proxy for downloads to handle CORS
 *
 * @param {string} url - The URL to fetch
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<Response>} - The fetch response
 */
export const fetchBlobUrl = async (url, options = {}) => {
  if (!url || typeof url !== "string") {
    throw new Error("Invalid URL provided");
  }

  // If it's an Azure Blob URL, try proxy first (force proxy for downloads), then fallback to direct
  if (isAzureBlobUrl(url)) {
    try {
      // Force proxy for fetches (downloads) to handle CORS; inline for preview (Content-Disposition: inline)
      const proxiedUrl = getBlobUrl(url, { forceProxy: true, inline: options.inline });
      // const response = await fetch1(proxiedUrl, options);
      const response = await gatewayAxios.get(proxiedUrl, options);

      // If proxy returns error status, try direct URL
      if (response.status !== 200 && response.status !== 201 ) {
        console.warn(
          "fetchBlobUrl: Proxy failed, falling back to direct URL",
          response.status
        );
        return gatewayAxios.get(url, options);
      }

      return response;
    } catch (e) {
      // If proxy fails completely, try direct URL as fallback
      console.warn("fetchBlobUrl: Proxy error, falling back to direct URL", e);
      return gatewayAxios.get(url, options);
    }
  }

  // For non-Azure URLs, fetch directly
  return gatewayAxios.get(url, options);
};

export const getPreviewUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  try {
    return getBlobUrl(url) || url;
  } catch (e) {
    console.warn("getPreviewUrl: Error processing URL, using original", e);
    return url;
  }
};

/**
 * Async: returns blob URL with _at temp token for gateway auth.
 * Use for iframe/img/video src where doc, pdf, html preview fails without auth.
 * Uses disposition=inline so backend returns inline (preview) instead of attachment (download).
 * @param {string} url - The original blob URL
 * @param {object} options - Same as getBlobUrl (forceProxy, inline), plus `force` (see addTokenToUrl -
 *   only needed when the URL is fetched by a third party like Microsoft's Office Online viewer, not
 *   loaded directly by the user's own browser).
 * @returns {Promise<string>} - URL with _at token for gateway
 */
export const getBlobUrlWithAuth = async (url, options = {}) => {
  const opts = { inline: true, ...options };
  const blobUrl = getBlobUrl(url, opts) || url;
  return addTokenToUrl(blobUrl, { force: options.force });
};

/**
 * Gateway /files/serve URLs need ?_at for <img> (no Authorization header). Axios callers use lib/axios.
 * Use this when persisting profile photo to session so avatars load with auth.
 */
export async function resolveSessionPhotoDisplayUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== "string") return "";
  try {
    return (await getBlobUrlWithAuth(rawUrl)) || getBlobUrl(rawUrl) || rawUrl;
  } catch (e) {
    console.warn("resolveSessionPhotoDisplayUrl:", e);
    return getBlobUrl(rawUrl) || rawUrl;
  }
}

/**
 * Safe wrapper for getBlobUrl that always returns a valid URL
 * Returns original URL if anything goes wrong
 *
 * @param {string} url - The original URL
 * @returns {string} - The proxied URL or original URL as fallback
 */
export const safeBlobUrl = (url) => {
  try {
    return getBlobUrl(url) || url;
  } catch (e) {
    console.warn("safeBlobUrl: Error processing URL, using original", e);
    return url;
  }
};

/**
 * Gets the file extension from a URL (e.g. ".csv", ".xlsx")
 * @param {string} urlOrFilename
 * @returns {string} Extension including the dot, or ""
 */
const getExtensionFromUrl = (urlOrFilename) => {
  const part = urlOrFilename?.split("/").pop()?.split("?")[0] || "";
  const lastDot = part.lastIndexOf(".");
  return lastDot > 0 ? part.slice(lastDot) : "";
};

/**
 * Ensures the download filename uses the extension from the actual URL being fetched,
 * so we never save e.g. CSV content with a .xlsx extension (which breaks Excel).
 * @param {string} url - The URL we are downloading from (source of truth for type)
 * @param {string} preferredName - Optional name from caller (e.g. "Athena Leads (1).xlsx")
 * @returns {string} Filename with extension matching the URL
 */
const getDownloadFilename = (url, preferredName) => {
  const urlExtension = getExtensionFromUrl(url);
  const fromUrl = url.split("/").pop()?.split("?")[0] || "download";
  if (!preferredName || !preferredName.trim()) return fromUrl;
  const base = preferredName.replace(/\.[^/.]+$/, "").trim() || fromUrl.replace(/\.[^/.]+$/, "") || "download";
  return urlExtension ? `${base}${urlExtension}` : preferredName;
};

/**
 * Downloads a file by fetching through proxy and saving with proper filename
 * Shows toast notifications for download progress
 * Extension is always taken from the download URL so it matches the actual content.
 *
 * @param {string} url - The file URL to download
 * @param {string} filename - The filename to save as (optional; extension will be corrected from URL)
 * @returns {Promise<void>}
 */
export const downloadBlobFile = async (url, filename) => {
  if (!url || typeof url !== "string") {
    console.error("downloadBlobFile: Invalid URL provided");
    return;
  }

  // Use extension from URL so saved file type matches content (avoids e.g. CSV saved as .xlsx)
  const extractedFilename = getDownloadFilename(url, filename || url.split("/").pop()?.split("?")[0] || "download");

  const downloadUrl = isAzureBlobUrl(url)
    ? `${PROXY_API_BASE}?url=${encodeURIComponent(stripAzureBlobSas(url))}`
    : url;

  // Import toast dynamically to avoid circular dependencies
  const { toast } = await import("react-hot-toast");
  
  // Show downloading toast
  const toastId = toast.loading(`Downloading ${extractedFilename}...`);

  try {
    // responseType: 'blob' ensures response.data is a Blob (handles CSV, binary, etc.)
    const response = await gatewayAxios.get(downloadUrl, { responseType: "blob" });

    if (response.status !== 200) {
      throw new Error(`Download failed: ${response.status}`);
    }

    // response.data is the Blob (axios follows 302 redirects automatically)
    const blob = response.data;
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = extractedFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    
    // Show success toast
    toast.success(`Downloaded ${extractedFilename}`, { id: toastId });
  } catch (error) {
    console.error("downloadBlobFile: Download failed", error);
    toast.error(`Download failed: ${extractedFilename}`, { id: toastId });
    // Fallback: open in new tab
    window.open(downloadUrl, "_blank");
  }
};

export default getBlobUrl;
